import {
  memo,
  type ComponentType,
  type MouseEvent,
  type ReactNode
} from "react";
import { Mail, Phone, Calendar, Clock } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/utilities/tailwind-utils";
import { formatDate } from "@/utilities/date-utils";
import { getInitials } from "@/utilities/typography-utils";
import { ProfileDisplayName } from "@/components/profiles/profile-display-name";
import {
  getEntityStatusBadge,
  getRoleBadge
} from "@/utilities/status-badge-utils";
import type { AdminUser } from "@/modules/admin/types/admin-types";
import type { ProfileCardMode } from "@/types/core";

export interface AdminUserProfileCardProps {
  user: AdminUser;
  mode?: ProfileCardMode;
  showAccent?: boolean;
  className?: string;
  onClick?: () => void;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  headerAction?: ReactNode;
}

/**
 * A display date, or null when the API sent none.
 *
 * `formatDate` already answers "" for an unparseable input, so the only thing left to
 * decide is that an absent date renders nothing rather than an empty row.
 */
function formatIsoDate(iso: string | undefined): string | null {
  if (!iso) return null;
  return formatDate(iso, "MONTH_DAY_COMMA_YEAR") || null;
}

function isPlaceholder(value: string): boolean {
  return !value || value === "—";
}

function InfoItem({
  icon: Icon,
  label,
  value
}: {
  icon: ComponentType<{ className?: string }>;
  label?: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <div className="flex items-center justify-center size-7 rounded-md bg-muted/60 shrink-0">
        <Icon className="size-3.5 text-muted-foreground" />
      </div>
      <div className="flex flex-col min-w-0 py-0.5">
        {label && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium leading-none mb-0.5">
            {label}
          </span>
        )}
        <span className="text-sm truncate">{value}</span>
      </div>
    </div>
  );
}

const CompactView = memo(function CompactView({
  user,
  headerAction,
  onNameClick
}: {
  user: AdminUser;
  headerAction?: ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const initials = getInitials(user.name || "?") || "?";
  const designation =
    !isPlaceholder(user.designation) ? user.designation : null;
  const lastSeenLabel = formatIsoDate(user.lastLoginAt);

  return (
    <div className="flex items-start gap-3">
      <Avatar className="size-11 shrink-0 ring-2 ring-primary/10">
        <AvatarFallback className="bg-primary/8 text-primary font-semibold text-xs">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <ProfileDisplayName compact onClick={onNameClick}>
                {user.name}
              </ProfileDisplayName>
              {getEntityStatusBadge(user.status)}
              {getRoleBadge(user.role)}
            </div>
            {designation && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {designation}
              </p>
            )}
          </div>
          {headerAction}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
          {user.email && (
            <span className="flex items-center gap-1 truncate max-w-[180px]">
              <Mail className="size-3 shrink-0" />
              <span className="truncate">{user.email}</span>
            </span>
          )}
          {!isPlaceholder(user.phone) && (
            <span className="flex items-center gap-1">
              <Phone className="size-3 shrink-0" />
              {user.phone}
            </span>
          )}
          {/*
            When the person was last here, not when their row was last written. This
            used to read "Last active" off `updated_at`, which moves when staff suspend
            an account -- so a suspension made a dormant user look freshly active.
          */}
          {lastSeenLabel && (
            <span className="flex items-center gap-1">
              <Calendar className="size-3 shrink-0" />
              Seen {lastSeenLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

const ExtendedView = memo(function ExtendedView({
  user,
  headerAction,
  onNameClick
}: {
  user: AdminUser;
  headerAction?: ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const initials = getInitials(user.name || "?") || "?";
  const createdLabel = formatIsoDate(user.createdAt);
  const updatedLabel = formatIsoDate(user.updatedAt);
  const lastSeenLabel = formatIsoDate(user.lastLoginAt);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-4">
        <Avatar className="size-14 shrink-0 ring-2 ring-primary/20 shadow-sm">
          <AvatarFallback className="bg-primary/8 text-primary font-bold text-base">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <ProfileDisplayName compact={false} onClick={onNameClick}>
                {user.name}
              </ProfileDisplayName>
              {!isPlaceholder(user.designation) && (
                <p className="text-sm text-muted-foreground truncate mt-0.5">
                  {user.designation}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              {getEntityStatusBadge(user.status)}
              {getRoleBadge(user.role)}
            </div>
          </div>
          {headerAction && <div className="mt-2">{headerAction}</div>}
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <InfoItem icon={Mail} label="Email" value={user.email || undefined} />
        <InfoItem
          icon={Phone}
          label="Phone"
          value={!isPlaceholder(user.phone) ? user.phone : undefined}
        />
        <InfoItem
          icon={Calendar}
          label="Last seen"
          value={lastSeenLabel ?? undefined}
        />
        <InfoItem
          icon={Clock}
          label="Last updated"
          value={updatedLabel ?? undefined}
        />
        <InfoItem
          icon={Clock}
          label="Member since"
          value={createdLabel ?? undefined}
        />
      </div>
    </div>
  );
});

export const AdminUserProfileCard = memo(function AdminUserProfileCard({
  user,
  mode = "compact",
  showAccent = true,
  className,
  onClick,
  onNameClick,
  headerAction
}: AdminUserProfileCardProps) {
  return (
    <Card
      className={cn(
        "overflow-hidden transition-all duration-200",
        mode === "extended" ? "pt-0" : "px-0 pt-0 pb-0",
        onClick &&
        "cursor-pointer hover:shadow-md hover:border-primary/20 active:scale-[0.995]",
        className
      )}
      onClick={onClick}
    >
      {showAccent && mode === "extended" && (
        <div className="h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
      )}
      <CardContent
        className={cn(mode === "extended" ? "pt-0 pb-0" : "py-3 px-3")}
      >
        {mode === "compact" ? (
          <CompactView
            user={user}
            headerAction={headerAction}
            onNameClick={onNameClick}
          />
        ) : (
          <ExtendedView
            user={user}
            headerAction={headerAction}
            onNameClick={onNameClick}
          />
        )}
      </CardContent>
    </Card>
  );
});

export default AdminUserProfileCard;
